import { importProvidersFrom, type EnvironmentProviders } from '@angular/core';
import { FormlyModule, type ConfigOption } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatToggleModule } from '@ngx-formly/material/toggle';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { emailValidator } from '../../validators/email';
import { urlValidator } from '../../validators/url';
import { CUSTOM_EXTENSIONS, CUSTOM_INPUTS, CUSTOM_WRAPPERS } from './index';

/**
 * The one Formly root configuration. Consumed through provideFormly() by app.config.ts (the app)
 * and .storybook/preview.ts (stories), so both render fields through the exact same
 * type/wrapper/extension registrations. Change field wiring HERE.
 */
export const FORMLY_ROOT_CONFIG: ConfigOption = {
  validators: [
    { name: 'email', validation: emailValidator },
    { name: 'url', validation: urlValidator },
  ],
  validationMessages: [
    { name: 'required', message: 'Dit veld is verplicht' },
    { name: 'min', message: ({ min }) => `Minimale waarde is ${min}` },
    { name: 'max', message: ({ max }) => `Maximale waarde is ${max}` },
    { name: 'minLength', message: ({ requiredLength }) => `Minimaal ${requiredLength} karakters vereist` },
    { name: 'maxLength', message: ({ requiredLength }) => `Maximaal ${requiredLength} karakters toegestaan` },
    { name: 'email', message: 'Voer een geldig e-mailadres in' },
    { name: 'pattern', message: 'Ongeldige waarde' },
  ],
  extensions: CUSTOM_EXTENSIONS,
  wrappers: CUSTOM_WRAPPERS,
  types: CUSTOM_INPUTS,
  extras: {
    renderFormlyFieldElement: true,
    // Re-evaluate `hideExpression` / `expressionProperties` only when the
    // model (or formControl.value) actually changes, not on every CD pass.
    // Default 'changeDetectionCheck' walks all fields' expressions every
    // tick — wasted work on wide forms. Safe here because our expressions
    // only read `field?.model?.[...]`; module-level conditional logic
    // runs through our own evaluator, not Formly's expressionProperties.
    checkExpressionOn: 'modelChange',
    // Defer rendering each field until it's first reached by CD —
    // collapsed tabs/wizard-steps/expansion-panels skip rendering until
    // opened. The FormControl + model entry are still created at config
    // time, so saved values + defaultValue still flow through.
    lazyRender: true,
  },
};

/**
 * Every provider Formly needs, as one unit.
 *
 * The Material modules are part of the contract, not an afterthought: `input`, `textarea` and
 * `select` are registered by FormlyMaterialModule, not by our CUSTOM_INPUTS. A consumer that
 * only calls FormlyModule.forRoot() renders exactly one broken field and then throws
 * `The type "input" could not be found` — which is what a hand-copied Storybook preview did
 * before this function existed.
 */
export function provideFormly(): EnvironmentProviders {
  return importProvidersFrom(
    FormlyModule.forRoot(FORMLY_ROOT_CONFIG),
    FormlyMaterialModule,
    FormlyMatToggleModule,
    FormlyMatDatepickerModule
  );
}
